import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Box from "@mui/material/Box";
import LayoutPage from "../../../templates/Layout/LayoutPage";
import ErrorLog from "../../../components/molecules/ErrorLog/ErrorLog";
import { simpleFetch } from "../../../utils/helpsers";
import { HarvestCycleStyles } from "./HarvestCycle.styles";

// The five status positions Eric defined on #620 (revised 2026-09-10), plus
// "Tracked elsewhere" for the sources that run on their own pipeline. Order
// runs by what needs attention first.
//
//   Blocked   we cannot release, for one of three reasons the row names:
//             no values landed, overdue at the source (the authority's own
//             release date has passed with nothing published), or a blocker
//             somebody flagged in the data. "Overdue" and "No values landed"
//             used to be positions of their own; they are reasons now.
//   Due now   expected within ~30 days, or published and ours to collect --
//             "actively monitoring".
//   In review found it, preparing it for release. It leaves here either to
//             Blocked or, once released, to Current. ("Ready for release" was
//             retired: in review already covers it.)
//   Current   not due for more than 30 days.
const STATUS = {
  harvested_blocked: {
    label: "Blocked",
    color: "#a93b2d",
    hint: "No values landed, overdue at the source, or a blocker in the data",
  },
  due: {
    label: "Due now",
    color: "#8f6414",
    hint: "Expected within 30 days, or published and ours to collect",
  },
  harvested_review: {
    label: "In review",
    color: "#0e6b78",
    hint: "Harvested, being prepared for release",
  },
  current: {
    label: "Current",
    color: "#2e7d5b",
    hint: "Not due for more than 30 days",
  },
  not_measured: { label: "Tracked elsewhere", color: "#78868c", hint: "Runs on a separate pipeline" },
};
const ORDER = Object.keys(STATUS);

// Why a Blocked source is blocked -- shown as a tag beside the chip.
const REASON = {
  no_values: "No values landed",
  overdue: "Overdue at source",
  source_blocker: "Blocker in the data",
};

// Is the year the IB catalog (Most Current Data) serves for this source the
// one we would expect? "expected" means MCD is on the newest year we hold
// values for; "stale_views" means it is behind that, which is a refresh
// problem rather than a harvest one.
const VERDICT = {
  expected: { label: "as expected", cls: "ok" },
  stale_views: { label: "views stale", cls: "stale" },
};

const FIRST_YEAR = 2004;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// API dates arrive as plain YYYY-MM-DD. Parsing them with `new Date()` would
// read them as UTC and shift the day backwards in US timezones, so split them.
const formatDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

// An occurrence is dated either to the day (an election, "2025-11-04") or to
// the year alone (the pre-2013 references, which cover a whole year or a whole
// decade at once). Both arrive in the same list, so both have to render.
const formatEventDate = (v) => (v && v.length > 4 ? formatDate(v) : v);

/**
 * Harvest Cycle — which datasources are current, due, in review, or blocked.
 *
 * Reads api.harvest_status(), which derives everything from the database: the
 * harvest ledger (api.hvd_source_domain, maintained by every data-refresh
 * migration) for what we hold, api.harvest_schedule for the authority's
 * publication calendar and where the pending release has got to, and the values
 * themselves for whether data actually landed.
 */
// Sortable columns. `value` pulls the comparable out of a source row; the
// default column keeps the original view (most urgent first, newest first).
const COLUMNS = [
  { key: "source", label: "Datasource", value: (s) => s.source.toLowerCase(), dir: "asc" },
  { key: "status", label: "Status", value: (s) => ORDER.indexOf(s.status), dir: "asc" },
  { key: "latest", label: "Last harvested", value: (s) => s.latest, dir: "desc" },
  {
    key: "mcd",
    label: "IB catalog (MCD)",
    value: (s) => (s.mcd_year === null || s.mcd_year === undefined ? null : s.mcd_year),
    dir: "desc",
  },
  {
    key: "expected_next",
    label: "Next expected",
    value: (s) => (s.status === "not_measured" ? null : s.expected_next),
    dir: "asc",
  },
  { key: "empty", label: "Variables with data", value: (s) => s.empty, dir: "desc" },
  { key: "harvests", label: "Harvest history", value: (s) => s.harvests, dir: "desc" },
];

const HarvestCycle = () => {
  const [sources, setSources] = useState(null);
  const [expanded, setExpanded] = useState({});
  // Which election-type date lists are open, keyed "<source>|<type>".
  const [openTypes, setOpenTypes] = useState({});
  const [sort, setSort] = useState({ key: "status", dir: "asc" });
  const [error, setError] = useState(null);

  const env = process.env.NODE_ENV;

  // Internal board. It reports on our own collection backlog — what we are late
  // on, what is blocked, what an authority has published that we have not taken
  // yet — so it is for super admins only, narrower than the Data Viewer's
  // "anyone but a plain api_user" rule. The nav link is hidden under the same
  // condition; this guard is what stops a direct /harvest visit.
  const { user } = useAuth0();
  const role = user ? user[`${process.env.REACT_APP_AUTH0_KEY}/role`] : undefined;
  const isSuperAdmin = role === "api_super_admin";
  const navigate = useNavigate();

  useEffect(() => {
    // Only send someone away once we actually have a user to judge; the route
    // sits behind withAuthenticationRequired, so this settles on first render.
    if (user && !isSuperAdmin) {
      navigate("/AccessDenied", { replace: true });
    }
  }, [user, isSuperAdmin, navigate]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    // simpleFetch resolves to response.data, and swallows API errors to undefined.
    simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/harvest_status`)
      .then((data) => {
        if (Array.isArray(data)) {
          // Three statuses were retired on the way to the five-position board:
          // "empty" and "overdue" became reasons a source is Blocked, and
          // "ready" folded into In review. Normalising on ingest keeps the page
          // from rendering an unknown status if it is ever served by an API
          // that predates those changes.
          const normalise = (s) => {
            if (s.status === "empty") return { ...s, status: "harvested_blocked", blocked_reason: s.blocked_reason || "no_values" };
            if (s.status === "overdue") return { ...s, status: "harvested_blocked", blocked_reason: s.blocked_reason || "overdue" };
            if (s.status === "harvested_ready") return { ...s, status: "harvested_review" };
            return s;
          };
          setSources(data.map(normalise));
        } else {
          setError({
            type: "Fetch Error",
            message: "Could not load harvest status from the API.",
          });
        }
      })
      .catch((err) => setError({ type: "Fetch Error", ...err }));
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return null;
  }

  if (error) {
    return <ErrorLog errorInfo={error} component={"Harvest Cycle"} environment={env} />;
  }

  const catalogYear = sources?.[0]?.catalog_year;
  const lastYear = catalogYear || new Date().getFullYear();
  const years = [];
  for (let y = FIRST_YEAR; y <= lastYear; y += 1) years.push(y);

  const counts = ORDER.reduce((acc, key) => {
    acc[key] = (sources || []).filter((s) => s.status === key).length;
    return acc;
  }, {});
  // A position nobody is in is not news. The strip grows as the workflow states
  // start being used, rather than showing a row of zeroes from day one.
  const shownStatuses = ORDER.filter((key) => counts[key] > 0);

  // How many sources are still running on a guessed cadence rather than the
  // authority's real calendar — the board's own remaining setup. Sources on a
  // separate pipeline are left out of both halves: no calendar is expected of
  // them, so counting them would overstate the work.
  const scheduled = (sources || []).filter((s) => s.status !== "not_measured");
  const estimatedCount = scheduled.filter((s) => s.estimated).length;

  const column = COLUMNS.find((c) => c.key === sort.key) || COLUMNS[1];

  const ordered = [...(sources || [])].sort((a, b) => {
    const av = column.value(a);
    const bv = column.value(b);
    // Rows with nothing to compare (e.g. no expected date) always sit last.
    if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
    if (bv === null || bv === undefined) return -1;

    let cmp = 0;
    if (typeof av === "string") cmp = av.localeCompare(bv);
    else cmp = av - bv;
    if (sort.dir === "desc") cmp = -cmp;
    if (cmp !== 0) return cmp;

    // Stable tiebreak so equal values keep a predictable order.
    if (a.latest !== b.latest) return b.latest - a.latest;
    return a.source.localeCompare(b.source);
  });

  const toggleSort = (col) =>
    setSort((prev) =>
      prev.key === col.key
        ? { key: col.key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key: col.key, dir: col.dir }
    );

  const cellClass = (source, year) => {
    const entry = (source.grid || []).find((g) => g.yr === year);
    if (!entry) return "";
    if (source.status === "not_measured") return " has";
    // An event source's values never enter api.variables_table, so wd is 0 by
    // construction. What a year holds is occurrences, and that is what it shows.
    if (source.tracking === "event") return entry.ev > 0 ? " full" : " has";
    if (entry.wd === entry.vars) return " full";
    if (entry.wd > 0) return " part";
    return " has";
  };

  const cellTitle = (source, year) => {
    const entry = (source.grid || []).find((g) => g.yr === year);
    if (!entry) return `${year}: no harvest`;
    if (source.tracking === "event") {
      return `${year}: ${entry.ev} election${entry.ev === 1 ? "" : "s"} harvested`;
    }
    return `${year}: ${entry.wd} of ${entry.vars} variables with data`;
  };

  const cadenceLabel = (n) => (n === 1 ? "yearly" : `every ${n} yrs`);

  // The one line under the chip that says why a source is where it is. What we
  // know for certain about our own pipeline is worth more than what we infer
  // about the authority's, so it is checked first.
  const statusDetail = (s) => {
    if (s.blocker_note) return s.blocker_note;
    if (s.available) return `Published ${formatDate(s.published_on)} — not yet harvested`;
    if (s.harvested_on) return `Harvested ${formatDate(s.harvested_on)}`;
    if (s.status === "not_measured") return null;
    if (s.expected_date) return `Expected ${formatDate(s.expected_date)}`;
    return null;
  };

  // The datasources a calculated variable depends on other than its own. These
  // are the dependencies that can go stale without this row changing at all.
  const elsewhere = (needs, own) => [
    ...new Set((needs || []).map((n) => n.source).filter((src) => src && src !== own)),
  ];

  // Variables grouped under their InsightBoard domain. Most sources sit in one
  // domain; the Cupp Report spans seven, which is exactly where the grouping
  // earns its place.
  const byDomain = (variables) => {
    const groups = [];
    (variables || []).forEach((v) => {
      const key = v.domain || "Unassigned";
      const last = groups[groups.length - 1];
      if (last && last.domain === key) last.items.push(v);
      else groups.push({ domain: key, items: [v] });
    });
    return groups;
  };

  // One variable line: name, whether it is loaded or calculated, what a
  // calculated one is waiting on, and the two years that reveal a gap.
  // `eventLatest` is passed for an event source, where the year pair is not a
  // measurement: those values are served from their own tables and never enter
  // api.variables_table, so last_with_data is null by construction and the pair
  // would read as a red "— / 2025" against data that is present and correct.
  // The latest occurrence is the fact that row can honestly carry.
  const varRow = (v, ownSource, eventLatest) => {
    const stale = !eventLatest && (v.last_with_data || 0) < v.attributed;
    const calculated = v.kind === "calculated";
    return (
      <div className="Harvest__vrow" key={v.v}>
        <span className="Harvest__vname">
          {v.name || v.v}
          <code className="Harvest__vcol">[{v.v}]</code>
          <span
            className={`Harvest__vkind${calculated ? " calc" : ""}`}
            title={
              calculated
                ? "Built from other InsightBoard variables — it can fall behind even when this source is current"
                : `Loaded from ${v.table}`
            }
          >
            {calculated ? "Calculated" : "Loaded"}
          </span>
          {/* Loaded and in the Glossary, but no card in the app shows it
              (api.variables.visible = false), so a stale year here is not
              worth chasing. */}
          {v.visible === false && (
            <span
              className="Harvest__vkind hidden"
              title="Not displayed in the app: api.variables.visible is false. Still loaded and still cited in the Glossary."
            >
              Hidden in app
            </span>
          )}
          {/* A hand-over between sources, read off the citations. The old
              source keeps its history and its last year; the note says where
              the variable went, so that year is not mistaken for a gap. */}
          {v.handed_to && (
            <span className="Harvest__vhandover">
              from {v.handed_to.from_year} delivered by <b>{v.handed_to.source}</b>
            </span>
          )}
          {v.took_over && (
            <span className="Harvest__vhandover">
              since {v.took_over.until_year + 1}; through {v.took_over.until_year} it came from{" "}
              <b>{v.took_over.source}</b>
            </span>
          )}
          {calculated && v.needs?.length > 0 && (
            <span className="Harvest__vneeds">
              needs {v.needs.map((n) => n.name || n.v).join(", ")}
              {/* A dependency reaching outside this datasource is the one that
                  can go stale without this row moving. */}
              {elsewhere(v.needs, ownSource).map((src) => (
                <span className="Harvest__vexternal" key={src}>
                  from {src}
                </span>
              ))}
            </span>
          )}
        </span>
        {eventLatest ? (
          <span className="Harvest__vyr" title="Most recent occurrence harvested">
            latest {formatEventDate(eventLatest)}
          </span>
        ) : (
          <span
            className={`Harvest__vyr${stale ? " bad" : ""}`}
            title="last year with data / last year attributed"
          >
            {v.last_with_data || "—"} / {v.attributed}
          </span>
        )}
      </div>
    );
  };

  // An event source registers one harvest per occurrence rather than one per
  // year, so its history is a list of dates grouped by the type the authority
  // publishes them under — for elections: General, Primary, Primary/Special,
  // Special, and the pre-2013 annual references. The breakdown hangs off the
  // one variable that carries the date; the rest land from the same files and
  // would only repeat it eleven times over.
  const eventBreakdown = (source) => {
    const ev = source.events;
    const dateVar =
      (source.variables || []).find((v) => v.v === ev.date_var) || source.variables?.[0];
    const others = (source.variables || []).length - (dateVar ? 1 : 0);

    return (
      <div className="Harvest__vars">
        <div className="Harvest__vdomain">{dateVar?.domain || "Harvest history"}</div>
        {dateVar && varRow(dateVar, source.source, ev.latest)}

        <div className="Harvest__types">
          {(ev.types || []).map((t) => {
            const key = `${source.source}|${t.type}`;
            const open = !!openTypes[key];
            // Only a dated occurrence with no attribution is a gap; the
            // pre-2013 references were never per-variable to begin with.
            const missing = t.gaps || 0;
            return (
              <div className="Harvest__type" key={t.type}>
                <button
                  type="button"
                  className="Harvest__typehead"
                  aria-expanded={open}
                  onClick={() => setOpenTypes((prev) => ({ ...prev, [key]: !prev[key] }))}
                >
                  <span className="Harvest__typename">{t.type}</span>
                  <span className="Harvest__typemeta">
                    {t.n} loaded · latest {formatEventDate(t.latest)}{" "}
                    {missing > 0 && (
                      <span
                        className="Harvest__warn"
                        title="Registered as a harvest but attributed to no variable, so it never reaches the board's counts"
                      >
                        {missing} unattributed
                      </span>
                    )}
                  </span>
                  <span className="Harvest__caret" aria-hidden="true">
                    {open ? "▾" : "▸"}
                  </span>
                </button>
                {open && (
                  <ul className="Harvest__dates">
                    {(t.dates || []).map((d) => (
                      <li key={d.d + d.title} className={d.mapped ? "" : "unmapped"}>
                        <span className="Harvest__dateval">{formatEventDate(d.d)}</span>
                        <span className="Harvest__datetitle">{d.title}</span>
                        {!d.mapped && (
                          <span className="Harvest__warn" title="No variables attributed to it">
                            unattributed
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {others > 0 && (
          <p className="Harvest__vnote">
            {others} other variables land from the same files.
          </p>
        )}
      </div>
    );
  };

  const topContentElement = (
    <p className="Harvest__intro">
      Every datasource behind InsightBoard: who publishes it, when the next release is expected,
      where the one we are chasing has got to, and which year the IB catalog is serving. Read
      live from the database.
    </p>
  );

  return (
    <LayoutPage
      pageTitle="Harvest Cycle"
      description="Which datasources are current, due for an update, in review, or blocked"
      type="dashboard"
      customClass="harvest-cycle"
      topContent={topContentElement}
    >
      <Box sx={HarvestCycleStyles}>
        {!sources && <p>Loading harvest status…</p>}

        {sources && (
          <>
            <div className="Harvest__summary">
              {shownStatuses.map((key) => (
                <div className="Harvest__stat" key={key} style={{ "--bar": STATUS[key].color }}>
                  <div className="Harvest__stat-n">{counts[key]}</div>
                  <div className="Harvest__stat-k">{STATUS[key].label}</div>
                  <div className="Harvest__stat-d">{STATUS[key].hint}</div>
                </div>
              ))}
            </div>

            <div className="Harvest__scroller">
              <table className="Harvest__table">
                <thead>
                  <tr>
                    {COLUMNS.map((col) => {
                      const active = sort.key === col.key;
                      return (
                        <th
                          key={col.key}
                          aria-sort={
                            active
                              ? sort.dir === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                          }
                        >
                          <button
                            type="button"
                            className={`Harvest__sort${active ? " active" : ""}`}
                            onClick={() => toggleSort(col)}
                          >
                            {col.key === "harvests" ? (
                              <>
                                {FIRST_YEAR} &rarr; {lastYear}
                              </>
                            ) : (
                              col.label
                            )}
                            <span className="Harvest__caret" aria-hidden="true">
                              {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
                            </span>
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((source) => {
                    const detail = statusDetail(source);
                    return (
                      <tr key={source.source}>
                        <td>
                          <div className="Harvest__src">{source.source}</div>
                          {source.authority && (
                            <div className="Harvest__srcauth">
                              {source.source_url ? (
                                <a
                                  href={source.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {source.authority}
                                </a>
                              ) : (
                                source.authority
                              )}
                            </div>
                          )}
                          <div className="Harvest__srcmeta">
                            {source.events ? (
                              <>
                                {source.events.first}–{source.latest} ·{" "}
                                {source.events.total} elections ·{" "}
                                {source.events.attributed} attributed
                              </>
                            ) : (
                              <>
                                {source.first}–{source.latest} · {source.harvests} harvests ·{" "}
                                {cadenceLabel(source.cadence)}
                              </>
                            )}
                          </div>
                          {source.variables?.length > 0 && (
                            <>
                              <button
                                type="button"
                                className="Harvest__toggle"
                                aria-expanded={!!expanded[source.source]}
                                onClick={() =>
                                  setExpanded((prev) => ({
                                    ...prev,
                                    [source.source]: !prev[source.source],
                                  }))
                                }
                              >
                                {expanded[source.source] ? "Hide" : "Show"}{" "}
                                {source.events
                                  ? "election history"
                                  : `${source.variables.length} variables`}
                              </button>
                              {expanded[source.source] &&
                                (source.events ? (
                                  eventBreakdown(source)
                                ) : (
                                  <div className="Harvest__vars">
                                    {byDomain(source.variables).map((group) => (
                                      <div key={group.domain}>
                                        <div className="Harvest__vdomain">{group.domain}</div>
                                        {group.items.map((v) => varRow(v, source.source))}
                                      </div>
                                    ))}
                                  </div>
                                ))}
                            </>
                          )}
                        </td>
                        <td>
                          <span className={`Harvest__chip status-${source.status}`}>
                            {STATUS[source.status].label}
                          </span>
                          {source.blocked_reason && REASON[source.blocked_reason] && (
                            <span className="Harvest__reason">{REASON[source.blocked_reason]}</span>
                          )}
                          {detail && <div className="Harvest__statusdetail">{detail}</div>}
                        </td>
                        <td className="Harvest__num">{source.latest}</td>
                        <td className="Harvest__num">
                          {/* The year Most Current Data serves for this source,
                              and whether that is the year we would expect. */}
                          {source.mcd_year === null || source.mcd_year === undefined ? (
                            "—"
                          ) : (
                            <>
                              {source.mcd_year}
                              {source.catalog_verdict && VERDICT[source.catalog_verdict] && (
                                <span
                                  className={`Harvest__verdict ${VERDICT[source.catalog_verdict].cls}`}
                                  title={
                                    source.catalog_verdict === "stale_views"
                                      ? `Values are held for ${source.held_year} but MCD still serves ${source.mcd_year} — the materialized views need a refresh`
                                      : `MCD serves the newest year with values (${source.held_year})`
                                  }
                                >
                                  {VERDICT[source.catalog_verdict].label}
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="Harvest__num">
                          {source.status === "not_measured" ? (
                            "—"
                          ) : (
                            <>
                              {source.expected_next}
                              {source.estimated && (
                                <span
                                  className="Harvest__est"
                                  title="No publication calendar recorded for this source yet — this is inferred from how often it has been harvested before."
                                >
                                  est.
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="Harvest__num">
                          {/* Election values are served from their own tables
                              and never enter api.variables_table, so a
                              with_data count would read 0 for data that is
                              present. The occurrences are what is measurable. */}
                          {source.events ? (
                            <span title="Elections attributed to at least one variable">
                              {source.events.attributed} elections
                            </span>
                          ) : (
                            <>
                              {source.with_data}/{source.vars}
                              {source.empty > 0 && source.status !== "not_measured" && (
                                <span className="Harvest__warn">{source.empty} empty</span>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          <div className="Harvest__grid">
                            {years.map((y) => (
                              <div
                                key={y}
                                className={`Harvest__cell${cellClass(source, y)}`}
                                title={cellTitle(source, y)}
                              />
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="Harvest__legend">
              <span>
                <i className="Harvest__sw" style={{ background: "#0e6b78" }} /> all variables have data
              </span>
              <span>
                <i className="Harvest__sw" style={{ background: "#b07d1a" }} /> some variables empty
              </span>
              <span>
                <i className="Harvest__sw" style={{ background: "#cbd6d9" }} /> harvested, values not
                measured here
              </span>
              <span>
                <i className="Harvest__sw" style={{ background: "#f0f2f3" }} /> no harvest that year
              </span>
            </div>

            <p className="Harvest__note">
              <em>Due now</em> means the authority is expected to publish within 30 days, or has
              published and the file is ours to collect. <em>Blocked</em> names its reason: no
              values landed, overdue at the source (the recorded release date passed with nothing
              published), or a blocker somebody flagged in the data. A source marked <em>est.</em>{" "}
              has no publication calendar recorded, so its next release is inferred from its past
              harvests and it can never be called overdue; {scheduled.length - estimatedCount} of{" "}
              {scheduled.length} now have a real date. <em>IB catalog (MCD)</em> is the year Most
              Current Data serves for the source; <em>views stale</em> means we hold a newer year
              than MCD shows and the materialized views need refreshing. Variable rows read{" "}
              <em>last year with data / last year attributed</em>; where those differ, the variable
              is offered for a year in which no district has a value.
            </p>
          </>
        )}
      </Box>
    </LayoutPage>
  );
};

export default HarvestCycle;
